import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  CssBaseline,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Checkbox,
  ListItemText,
  Tooltip,
  FormControlLabel,
  TextField,
  InputAdornment,
  Collapse,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { useStoreData } from '../useStoreData';
import dataStore from '../dataStore';
import { useOddsFormat, fmtPrice } from '../oddsFormat';
import { BOOK_GROUPS, booksInGroup, isGroupValue, groupFromValue, GROUP_PREFIX } from '../bookGroups';
import { useLogoMaps } from '../useLogoMaps';
import GameLogModal from '../components/GameLogModal';
import OddsHistoryModal, { useOddsHistory } from '../components/OddsHistoryModal';

// ══════════════════════════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════════════════════════

const PREFERRED_BOOK_ORDER = [
  'DraftKings', 'Pinnacle', 'Bookmaker', 'Circa', 'FanDuel', 'Bet365',
  'theScore Bet', 'Caesars', 'BetMGM', 'BetRivers', 'Bovada', 'Hard Rock',
  'Fanatics', 'Prophet Exchange', 'NoVig', 'Polymarket', 'Kalshi', 'LowVig',
];

const bookmakerLinks = {
  'Prophet Exchange': 'https://prophetx.onelink.me/E5Yi/kxa7a4q7',
  'NoVig': 'https://novig.onelink.me/JHQQ/1vgeffy0',
  'DraftKings': 'https://dksb.sng.link/As9kz/85o9?_dl=https%3A%2F%2Fsportsbook.draftkings.com%2Fgateway%3Fs%3D356902394%26wpcid%3D239969%26wpcn%3DBet5Win150%26wpsrc%3DBetsperts%26wpcrid%3D%26wpscn%3D&pcid=239969&psn=Betsperts&pcn=Bet5Win150_4For4',
  'FanDuel': 'https://wlfanduelus.adsrv.eacdn.com/C.ashx?btag=a_21545b_16c_&affid=11060&siteid=21545&adid=16&c=',
  'Bet365': 'https://www.bet365.com/olp/open-account?affiliate=365_02038136',
  'BetMGM': 'https://mediaserver.betmgmpartners.com/renderBanner.do?zoneId=1700764',
  'Caesars': 'https://wlwilliamhillus.adsrv.eacdn.com/C.ashx?btag=a_12260b_2091c_&affid=47&siteid=12260&adid=2091&c=',
  'Fanatics': 'https://go.affiliationcloud.com/0002JY',
};

// Default bet type per sport (market ID)
const DEFAULT_BET_TYPE = {
  NBA: 'player_points',
  NFL: 'player_passing_yards',
  MLB: 'player_strikeouts',
  CFB: 'player_passing_yards',
};

const periodTypesBySport = {
  NBA: ['Full Game', 'First Half', 'Second Half', 'First Quarter'],
  CBB: ['Full Game', 'First Half', 'Second Half', 'First Quarter'],
  WNBA: ['Full Game', 'First Half', 'Second Half', 'First Quarter'],
  MLB: ['Full Game'],
  NHL: ['Full Game', 'First Period', 'Second Period', 'Third Period'],
  NFL: ['Full Game', 'First Half', 'Second Half', 'First Quarter', 'Second Quarter', 'Third Quarter', 'Fourth Quarter'],
  CFB: ['Full Game', 'First Half', 'Second Half', 'First Quarter', 'Second Quarter', 'Third Quarter', 'Fourth Quarter'],
  PGA: ['Full Game'],
};

// ══════════════════════════════════════════════════════════
//  THEME + STATIC STYLES — hoisted outside component
// ══════════════════════════════════════════════════════════

const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
    MuiTabs: {
      styleOverrides: {
        root: { backgroundColor: '#121212' },
        indicator: { backgroundColor: '#ff1744', height: '2px' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#121212',
          borderRadius: '8px 8px 0 0',
          transition: 'background-color 0.2s, color 0.2s',
          '&.Mui-selected': { color: '#ffffff', backgroundColor: '#121212' },
          '&:hover': { backgroundColor: '#1a1a1a' },
        },
      },
    },
  },
});

const SX_COL_OUTER = { display: 'flex', flexDirection: 'column', gap: '0', justifyContent: 'center', height: '100%' };
const SX_EMPTY_CELL = { textAlign: 'right', pr: '12px' };
const STYLE_BEST_IMG = { width: '30px', height: '30px', objectFit: 'contain' };
const FIXED_COLUMNS = ['eventName', 'eventStartFormatted', 'bestOdds'];

// Format limits for display (e.g. "$1.5k", "$998")
const formatLimitMax = (limits) => {
  const max = limits?.max;
  if (max == null) return null;
  return max >= 1000 ? `$${(max / 1000).toFixed(1).replace(/\.0$/, '')}k` : `$${Math.round(max)}`;
};

const THROTTLE_MS = 500;


// ── Memoized bookmaker cell ──
// DataGridPro re-renders every visible cell on each column-drag hover step
// (and on every data flush). Memoizing the cell on its inputs makes those
// re-renders near-free when the underlying row objects haven't changed.
const _outcomeSxCache = new Map();
const outcomeBoxSx = (isBest, clickable, height) => {
  const k = `${isBest ? 1 : 0}${clickable ? 1 : 0}${height}`;
  let sx = _outcomeSxCache.get(k);
  if (!sx) {
    sx = {
      height: `${height}px`, display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%',
      cursor: clickable ? 'pointer' : 'default',
      border: isBest ? '2px solid #00cc00' : 'none',
      '&:hover': clickable ? { backgroundColor: 'rgba(255,255,255,0.05)' } : {},
    };
    _outcomeSxCache.set(k, sx);
  }
  return sx;
};
const SX_ODDS_TXT_M = { fontSize: '12px', textAlign: 'right', pr: '4px', lineHeight: 1.2 };
const SX_ODDS_TXT_D = { fontSize: '14px', textAlign: 'right', pr: '12px', lineHeight: 1.2 };
const SX_VOL_TXT_M = { color: '#888888', fontSize: '0.65rem', textAlign: 'right', pr: '4px', lineHeight: 1 };
const SX_VOL_TXT_D = { color: '#888888', fontSize: '0.65rem', textAlign: 'right', pr: '12px', lineHeight: 1 };
const SX_EMPTY_TXT = { fontSize: '14px' };

const OutcomeBox = ({ value, limits, updatedAt, isBest, meta, field, showVolume, isMobile, height, onOpen }) => {
  const v = showVolume ? formatLimitMax(limits) : null;
  const flash = getFlashStyle(updatedAt);
  return (
    <Box sx={outcomeBoxSx(isBest, !!value, height)} style={flash} className={flash ? 'ss-flash' : undefined}
      onClick={() => meta && onOpen(meta, field)}>
      <Typography sx={isMobile ? SX_ODDS_TXT_M : SX_ODDS_TXT_D}>{value || ''}</Typography>
      {v ? <Typography sx={isMobile ? SX_VOL_TXT_M : SX_VOL_TXT_D}>{v}</Typography> : null}
    </Box>
  );
};

const BookCell = React.memo(function BookCell({ bookOdds, best, field, noPoints, showVolume, isMobile, onOpen }) {
  if (!bookOdds) return <Box sx={SX_EMPTY_CELL}><Typography sx={SX_EMPTY_TXT} /></Box>;
  const outcomes = ['Over', 'Under'].filter(o => bookOdds[o]);
  return (
    <Box sx={SX_COL_OUTER}>
      {outcomes.map(outcome => (
        <OutcomeBox key={outcome} value={bookOdds[outcome]} limits={bookOdds.limits?.[outcome]}
          updatedAt={bookOdds.updatedDates?.[outcome]} isBest={field === best?.[outcome]}
          meta={bookOdds.meta?.[outcome]} field={field}
          showVolume={showVolume} isMobile={isMobile} height={noPoints ? 70 : 35} onOpen={onOpen} />
      ))}
    </Box>
  );
});

// ── Flash-fade: pure CSS animation (consistent with Odds.js — no JS timer) ──
const FLASH_DURATION_SEC = 300;
const FLASH_KEYFRAMES_ID = 'flash-fade-keyframes';

if (typeof document !== 'undefined' && !document.getElementById(FLASH_KEYFRAMES_ID)) {
  const style = document.createElement('style');
  style.id = FLASH_KEYFRAMES_ID;
  // Compositor-friendly flash: the tint lives on a pseudo-element and we animate
  // its OPACITY (GPU-composited), not background-color (main-thread style recalc
  // every frame for every flashing cell — this was saturating the main thread).
  style.textContent = `
    @keyframes flashFade { from { opacity: 1; } to { opacity: 0; } }
    .ss-flash { position: relative; isolation: isolate; }
    .ss-flash::before {
      content: ''; position: absolute; inset: 0; z-index: -1; pointer-events: none;
      background-color: rgb(13, 71, 161);
      will-change: opacity;
      animation: flashFade ${FLASH_DURATION_SEC}s linear forwards;
      animation-delay: var(--ss-flash-delay, 0s);
    }
  `;
  document.head.appendChild(style);
}

function getFlashStyle(updatedAt) {
  if (!updatedAt) return undefined;
  const ageSec = (Date.now() - new Date(updatedAt).getTime()) / 1000;
  if (ageSec < 0 || ageSec > FLASH_DURATION_SEC) return undefined;
  return { '--ss-flash-delay': `-${ageSec.toFixed(0)}s` };
}

// ── Shared Intl date/time formatters (reused across renders) ──
const DATE_FMT = new Intl.DateTimeFormat('en-US', { year: '2-digit', month: 'numeric', day: 'numeric' });
const TIME_FMT = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

// ── Hoisted DataGridPro sx (consistent with Odds.js) ──
const DATA_GRID_SX = {
  '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1e1e1e', borderBottom: '2px solid #444', position: 'sticky', top: '0', zIndex: 2 },
  '& .MuiDataGrid-columnHeader': { userSelect: 'none', cursor: 'move', outline: 'none !important', '&:focus': { outline: 'none !important' }, '&:focus-within': { outline: 'none !important' } },
  '& .MuiDataGrid-columnHeader--alignRight': { justifyContent: 'flex-end !important' },
  '& .MuiDataGrid-columnHeaderTitleContainer': { outline: 'none !important', '&:focus': { outline: 'none !important' } },
  '& .MuiDataGrid-cell': { color: '#ffffff !important', userSelect: 'none', padding: 0, borderRight: '1px solid rgba(255, 255, 255, 0.3)' },
  '& .MuiDataGrid-cell:last-child': { borderRight: 'none' },
  '& .MuiDataGrid-cell:focus': { outline: 'none' },
  '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
  '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
  '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#1a1a1a', '&:hover': { backgroundColor: '#1a1a1a' } },
  '& .MuiDataGrid-columnSeparator': { visibility: 'visible', color: 'rgba(255, 255, 255, 0.3)' },
  '& .MuiDataGrid-virtualScroller': { overflowY: 'auto !important' },
  '& .MuiDataGrid-root': { overflowY: 'auto !important', margin: 0 },
  '& .MuiDataGrid-main': { padding: 0 },
  '& .MuiDataGrid-pinnedColumns, & .MuiDataGrid-pinnedColumnHeaders': { backgroundColor: '#121212' },
  '& .MuiDataGrid-footerContainer': { backgroundColor: '#1e1e1e', color: '#ffffff !important', position: 'sticky', bottom: 0, zIndex: 1, minHeight: '26px', height: '26px', borderTop: '1px solid #444' },
  '& .MuiDataGrid-rowCount': { fontSize: '0.75rem', margin: 0 },
  '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { color: '#ffffff !important' },
  '& .MuiSelect-icon': { color: '#ffffff !important' },
  '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
  '& .MuiDataGrid-columnHeaderTitle': { color: '#ffffff !important', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 },
  '& .MuiDataGrid-columnHeaderDraggableContainer': { cursor: 'move' },
  // While a header is being dragged, the body ignores the pointer so :hover state
  // can't churn — otherwise every native `drag` event forces a full-grid style recalc.
  '&.ss-col-dragging .MuiDataGrid-virtualScrollerContent': { pointerEvents: 'none' },
  '&.ss-col-dragging .MuiDataGrid-row:hover': { backgroundColor: 'transparent' },
};

// ══════════════════════════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════════════════════════

const Props = ({ isAuthenticated = false }) => {
  const sportsbooks = useStoreData('sportsbooks');
  const leaguesConfig = useStoreData('leagues') || [];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Mobile-only: dropdowns/checkboxes collapse behind the filter toggle; search stays visible
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const apiRef = useGridApiRef();

  // ── Logo lookup maps (resolved from reference tables, not per-record) ──
  const { bookLogos, teamMap } = useLogoMaps();

  // ── Available books: always derived from server-provided sportsbooks config (ingest=1) ──
  const availableBooks = useMemo(() => {
    const orderIndex = {};
    PREFERRED_BOOK_ORDER.forEach((name, i) => { orderIndex[name] = i; });

    return sportsbooks.map(sb => sb.name).filter(Boolean).sort((a, b) => {
      const ia = orderIndex[a] !== undefined ? orderIndex[a] : 9999;
      const ib = orderIndex[b] !== undefined ? orderIndex[b] : 9999;
      if (ia !== ib) return ia - ib;
      return a.localeCompare(b);
    });
  }, [sportsbooks]);

  // ── Filter state (persisted to localStorage) ──
  const [sport, setSport] = useState(() => localStorage.getItem('propsSport') || 'NFL');
  const [betType, setBetType] = useState(() => localStorage.getItem('propsBetType') || 'player_points');
  const [periodType, setPeriodType] = useState(() => localStorage.getItem('propsPeriodType') || 'Full Game');
  const [selectedMatchup, setSelectedMatchup] = useState(() => localStorage.getItem('propsSelectedMatchup') || 'All');
  const [searchText, setSearchText] = useState('');

  // ── Odds subscription (ref-based, NOT useStoreData) ──
  // useStoreData re-renders this component on EVERY odds message and copies the
  // whole odds Map (Array.from) inside render. During rapid update periods that
  // is dozens of full-component renders per second, which starves the native
  // column-drag. Here we subscribe directly, keep the store off the render path,
  // and materialize the array at most once per THROTTLE_MS.
  const readBets = useCallback(() => {
    const data = dataStore.get('odds');
    const arr = data instanceof Map ? Array.from(data.values()) : (data || []);
    return arr;
  }, []);
  const [throttledBets, setThrottledBets] = useState(readBets);
  const throttleRef = useRef(null);
  const dirtyRef = useRef(false);
  // latestBetsRef is a lazy accessor so we never materialize the array unless we flush
  const latestBetsRef = useRef(null);
  latestBetsRef.current = readBets;

  // Column-drag guard: while a header is being dragged, data updates are held
  // so the grid isn't re-rendered mid-drag (which drops the drag / freezes).
  const dragActiveRef = useRef(false);
  const dragPendingRef = useRef(false);

  useEffect(() => {
    const flush = () => {
      dirtyRef.current = false;
      setThrottledBets(readBets());
    };
    const scheduleTrailing = () => {
      throttleRef.current = setTimeout(() => {
        throttleRef.current = null;
        if (!dirtyRef.current) return;
        if (dragActiveRef.current) { dragPendingRef.current = true; return; }
        flush();
        scheduleTrailing();
      }, THROTTLE_MS);
    };
    const unsubscribe = dataStore.subscribe('odds', () => {
      dirtyRef.current = true;
      if (dragActiveRef.current) { dragPendingRef.current = true; return; }
      if (throttleRef.current) return; // trailing edge will pick it up
      flush();                           // leading edge
      scheduleTrailing();
    });
    return () => {
      unsubscribe();
      if (throttleRef.current) { clearTimeout(throttleRef.current); throttleRef.current = null; }
    };
  }, [readBets]);

  // (Flash-fade handled by pure CSS animation — no JS timer needed)

  // ── Selected books ──
  const [selectedBooks, setSelectedBooks] = useState([]);

  useEffect(() => {
    if (availableBooks.length === 0) return;
    const saved = localStorage.getItem(`propsSelectedBooks_${isMobile ? 'mobile' : 'desktop'}`);
    if (saved) {
      try {
        const valid = JSON.parse(saved).filter(b => availableBooks.includes(b));
        if (valid.length > 0) { setSelectedBooks(valid); return; }
      } catch (e) { /* fall through */ }
    }
    setSelectedBooks([...availableBooks]);
  }, [availableBooks, isMobile]);

  useEffect(() => {
    localStorage.setItem(`propsSelectedBooks_${isMobile ? 'mobile' : 'desktop'}`, JSON.stringify(selectedBooks));
  }, [selectedBooks, isMobile]);

  // ── Persist filter state ──
  useEffect(() => { localStorage.setItem('propsSport', sport); }, [sport]);
  useEffect(() => { localStorage.setItem('propsBetType', betType); }, [betType]);
  useEffect(() => { localStorage.setItem('propsPeriodType', periodType); }, [periodType]);
  useEffect(() => { localStorage.setItem('propsSelectedMatchup', selectedMatchup); }, [selectedMatchup]);

  // ── Volume toggle (persisted to localStorage) ──
  // -- Show live toggle (off by default; requires subscription) --
  const [showLive, setShowLive] = useState(() => localStorage.getItem('propsShowLive') === 'true');
  useEffect(() => { localStorage.setItem('propsShowLive', JSON.stringify(showLive)); }, [showLive]);
  // Effective value: persisted preference only honored for subscribers
  const liveVisible = isAuthenticated && showLive;

  // ── Odds format (global; toggled from the sidebar) ──
  const oddsFormat = useOddsFormat();

  const [showVolume, setShowVolume] = useState(() => localStorage.getItem('propsShowVolume') === 'true');
  const showVolumeRef = useRef(showVolume);
  showVolumeRef.current = showVolume;
  useEffect(() => { localStorage.setItem('propsShowVolume', JSON.stringify(showVolume)); }, [showVolume]);

  // ── League options: from server-provided config (ingest=1 from DB) ──
  const leagueOptions = useMemo(() => {
    if (leaguesConfig.length > 0) {
      return leaguesConfig.map(l => l.name || l.id).filter(Boolean).sort();
    }
    // Fallback to data-derived leagues if config not loaded yet
    const fromData = [...new Set((throttledBets || []).map(b => b.league).filter(Boolean))];
    return fromData.length > 0 ? fromData.sort() : ['NBA'];
  }, [leaguesConfig, throttledBets]);

  // Reset sport if saved value no longer exists in available leagues
  useEffect(() => {
    if (leagueOptions.length > 0 && !leagueOptions.includes(sport)) {
      setSport(leagueOptions[0]);
    }
  }, [leagueOptions, sport]);

  const periodTypes = useMemo(() => periodTypesBySport[sport] || ['Full Game'], [sport]);

  useEffect(() => {
    if (!periodTypes.includes(periodType)) setPeriodType(periodTypes[0] || 'Full Game');
  }, [periodTypes, periodType]);

  useEffect(() => { setSelectedMatchup('All'); }, [sport]);

  // ── Game Log Modal ──
  const [gameLogModalOpen, setGameLogModalOpen] = useState(false);
  const [selectedPlayerData, setSelectedPlayerData] = useState(null);
  const [gameLogData, setGameLogData] = useState(null);
  const [gameLogLoading, setGameLogLoading] = useState(false);
  const gameLogSportRef = useRef('NBA');

  // ── Odds history modal ──
  const { historyOpen, historyData, historyLoading, historyMeta, openOddsHistory, closeOddsHistory } = useOddsHistory();

  const handlePlayerClick = async (rowData) => {
    if (sport !== 'NFL' && sport !== 'NBA') return;
    const personId = rowData.playerId;
    if (!personId) return;

    const league = sport === 'NBA' ? 'nba' : 'nfl';
    gameLogSportRef.current = sport;
    setGameLogLoading(true);

    try {
      const response = await fetch(`https://ws.betpros.ai:8080/api/player-game-logs/${league}/${personId}`);
      if (!response.ok) { setGameLogLoading(false); return; }

      const data = await response.json();
      if (data && data.gameLogs && data.gameLogs.length > 0) {
        setSelectedPlayerData({
          PlayerName: data.playerName,
          Position: data.position,
          Team: data.team,
          teamLogo: teamMap[rowData.homeTeamId]?.logo || teamMap[rowData.awayTeamId]?.logo || '',
          photoUrl: data.photoUrl,
          Height: data.Height,
          Weight: data.Weight,
          Age: data.Age,
          College: data.College,
          Experience: data.Experience,
          EventStart: rowData.eventStart,
          AwayShort: teamMap[rowData.awayTeamId]?.abbreviation || '',
          HomeShort: teamMap[rowData.homeTeamId]?.abbreviation || '',
          AwayLogo: teamMap[rowData.awayTeamId]?.logo || '',
          HomeLogo: teamMap[rowData.homeTeamId]?.logo || '',
        });
        setGameLogData(data.gameLogs);
        setGameLogModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching game log:', error);
    } finally {
      setGameLogLoading(false);
    }
  };

  const handleCloseModal = () => {
    setGameLogModalOpen(false);
    setSelectedPlayerData(null);
    setGameLogData(null);
  };

  // ── Books filter handlers ──
  const handleBooksFilterChange = useCallback((event) => {
    const value = event.target.value;
    const clicked = value[value.length - 1];
    if (clicked === 'all') {
      setSelectedBooks(prev => prev.length === availableBooks.length ? [] : [...availableBooks]);
    } else if (isGroupValue(clicked)) {
      // Combo preset: select exactly the books in this group (that exist here)
      const group = groupFromValue(clicked);
      if (group) setSelectedBooks(booksInGroup(group, availableBooks));
    } else {
      setSelectedBooks(value);
    }
  }, [availableBooks]);

  const renderBooksValue = useCallback((selected) => {
    if (!selected.length) return 'None';
    if (selected.length === availableBooks.length) return 'All';
    if (selected.length > 1) return `Multiple (${selected.length})`;
    return selected[0];
  }, [availableBooks]);

  // ══════════════════════════════════════════════════════════
  //  DERIVE BET TYPES + MATCHUPS from data
  // ══════════════════════════════════════════════════════════

  const { betTypes, matchups, marketNameMap } = useMemo(() => {
    if (!Array.isArray(throttledBets)) return { betTypes: [], matchups: [], marketNameMap: new Map() };

    const types = new Set();
    const marketNameMap = new Map();
    const matchupData = new Map();

    throttledBets.forEach(bet => {
      if (!bet || bet.marketCategory !== 'Player Prop' || bet.league !== sport) return;
      if (bet.periodType !== periodType) return;

      if (bet.market) {
        types.add(bet.market);
        if (bet.marketName && !marketNameMap.has(bet.market)) {
          marketNameMap.set(bet.market, bet.marketName);
        }
      }

      // Build short matchup key from abbreviations
      const shortName = (teamMap[bet.awayTeamId]?.abbreviation && teamMap[bet.homeTeamId]?.abbreviation)
        ? `${teamMap[bet.awayTeamId]?.abbreviation} @ ${teamMap[bet.homeTeamId]?.abbreviation}`
        : bet.eventName;

      if (shortName && bet.eventStart) {
        const existing = matchupData.get(shortName);
        const betTime = new Date(bet.eventStart).getTime();
        if (!existing || betTime < existing.eventStart) {
          matchupData.set(shortName, {
            eventStart: betTime,
            awayTeamId: bet.awayTeamId || '',
            homeTeamId: bet.homeTeamId || '',
            awayAbbr: teamMap[bet.awayTeamId]?.abbreviation || '',
            homeAbbr: teamMap[bet.homeTeamId]?.abbreviation || '',
            eventName: bet.eventName || '',
          });
        }
      }
    });

    // Sort bet types by display name
    const sortedTypes = Array.from(types).sort((a, b) => {
      const nameA = marketNameMap.get(a) || a;
      const nameB = marketNameMap.get(b) || b;
      return nameA.localeCompare(nameB);
    });

    const sortedMatchups = Array.from(matchupData.entries())
      .sort((a, b) => {
        const timeA = a[1].eventStart || Number.MAX_SAFE_INTEGER;
        const timeB = b[1].eventStart || Number.MAX_SAFE_INTEGER;
        return timeA - timeB;
      })
      .map(([key, data]) => ({
        shortName: key,
        awayTeamId: data.awayTeamId,
        homeTeamId: data.homeTeamId,
        awayAbbr: data.awayAbbr,
        homeAbbr: data.homeAbbr,
        eventName: data.eventName,
      }));

    return { betTypes: sortedTypes, matchups: sortedMatchups, marketNameMap };
  }, [throttledBets, sport, periodType, teamMap]);

  // Set default betType when betTypes change
  useEffect(() => {
    if (betTypes.length > 0 && (!betType || !betTypes.includes(betType))) {
      const sportDefault = DEFAULT_BET_TYPE[sport];
      if (sportDefault && betTypes.includes(sportDefault)) {
        setBetType(sportDefault);
      } else {
        setBetType(betTypes[0]);
      }
    } else if (betTypes.length === 0) {
      setBetType('');
    }
  }, [betTypes, betType, sport]);

  // ══════════════════════════════════════════════════════════
  //  TABLE DATA — group bets into player rows
  //  Also derives isNoPointsBetType in the same pass (avoids
  //  a redundant second scan of the full bets array).
  // ══════════════════════════════════════════════════════════

  const { rows: tableRows, isNoPointsBetType } = useMemo(() => {
    if (!Array.isArray(throttledBets)) return { rows: [], isNoPointsBetType: false };

    const filteredBets = throttledBets.filter(bet => {
      if (!bet) return false;
      if (bet.marketCategory !== 'Player Prop' || bet.league !== sport ||
          bet.market !== betType || bet.periodType !== periodType) return false;
      if (!liveVisible && bet.isLive) return false;
      // Early matchup filter — avoids building rows we'll discard
      if (selectedMatchup !== 'All') {
        const matchupName = (teamMap[bet.awayTeamId]?.abbreviation && teamMap[bet.homeTeamId]?.abbreviation)
          ? `${teamMap[bet.awayTeamId]?.abbreviation} @ ${teamMap[bet.homeTeamId]?.abbreviation}` : bet.eventName;
        if (matchupName !== selectedMatchup) return false;
      }
      return true;
    });

    // ── Derive isNoPointsBetType from filteredBets (single pass) ──
    let hasAnyPoints = false;
    for (let i = 0; i < filteredBets.length; i++) {
      const p = filteredBets[i].points;
      if (p !== null && p !== undefined && p !== 0 && p !== '0') {
        hasAnyPoints = true;
        break;
      }
    }
    const noPoints = filteredBets.length > 0 && !hasAnyPoints;

    // Group by event + player
    const groups = {};

    filteredBets.forEach(bet => {
      const key = `${bet.fixture_id || bet.eventName}-${bet.playerName || ''}`;
      if (!groups[key]) {
        const shortName = (teamMap[bet.awayTeamId]?.abbreviation && teamMap[bet.homeTeamId]?.abbreviation)
        ? `${teamMap[bet.awayTeamId]?.abbreviation} @ ${teamMap[bet.homeTeamId]?.abbreviation}`
        : bet.eventName;
        groups[key] = {
          eventName: bet.eventName,
          player: bet.playerName || '',
          position: bet.playerPosition || '',
          shortName,
          startTime: bet.eventStart,
          awayTeamId: bet.awayTeamId || '',
          homeTeamId: bet.homeTeamId || '',
          awayAbbr: teamMap[bet.awayTeamId]?.abbreviation || '',
          homeAbbr: teamMap[bet.homeTeamId]?.abbreviation || '',
          bets: [],
        };
      }
      groups[key].bets.push(bet);
    });

    const rowsArray = [];
    const booksToRender = selectedBooks;

    Object.entries(groups).forEach(([groupKey, group]) => {
      const startDate = group.startTime ? new Date(group.startTime) : null;

      const row = {
        id: groupKey,
        eventName: group.eventName,
        isLive: group.bets.some(b => b.isLive),
        playerName: group.player,
        position: group.position,
        displayName: group.player,
        shortName: group.shortName,
        awayTeamId: group.awayTeamId,
        homeTeamId: group.homeTeamId,
        awayAbbr: group.awayAbbr,
        homeAbbr: group.homeAbbr,
        eventStart: group.startTime,
        playerId: group.bets[0]?.playerId,
        eventStartFormatted: {
          date: startDate ? DATE_FMT.format(startDate) : '',
          time: startDate ? TIME_FMT.format(startDate) : '',
        },
        bookmakerOdds: {},
        bestOddsBookmakers: { Over: '', Under: '' },
      };

      // Build outcomes per book
      const outcomes = { Over: {}, Under: {} };
      const outcomeDetails = { Over: {}, Under: {} };
      const outcomeUpdatedDates = { Over: {}, Under: {} };

      group.bets.forEach(bet => {
        const outcome = bet.side === 'Over' ? 'Over' : 'Under';
        outcomes[outcome][bet.bookName] = bet.price;
        outcomeDetails[outcome][`${bet.bookName}Points`] = bet.points;
        outcomeDetails[outcome][`${bet.bookName}Logo`] = bookLogos[bet.bookName] || '';
        const hasPoints = bet.points !== null && bet.points !== undefined && bet.points !== 0 && bet.points !== '0';
        outcomeDetails[outcome][`${bet.bookName}LineDisplay`] = hasPoints ? `${outcome === 'Over' ? 'o' : 'u'}${bet.points}` : '';
        outcomeUpdatedDates[outcome][bet.bookName] = bet.updatedAt;
        outcomeDetails[outcome][`${bet.bookName}Limits`] = bet.limits;
        outcomeDetails[outcome][`${bet.bookName}Meta`] = {
          fixture_id: bet.fixture_id,
          market_id: bet.market_id,
          selection: bet.selection,
          selection_line: bet.selection_line,
          player_id: bet.playerId || '',
          points: bet.points,
          playerName: bet.playerName || '',
          marketDisplay: bet.marketName || betType || '',
          league: sport,
          awayTeam: row.awayAbbr,
          homeTeam: row.homeAbbr,
          awayTeamLogo: teamMap[row.awayTeamId]?.logo || '',
          homeTeamLogo: teamMap[row.homeTeamId]?.logo || '',
          eventStart: row.eventStart,
          bookLogo: bookLogos[bet.bookName] || '',
        };
      });

      // Best odds from selected books
      const bestOdds = {};
      ['Over', 'Under'].forEach(outcome => {
        const entries = [];
        booksToRender.forEach(book => {
          const price = outcomes[outcome][book] ? parseFloat(outcomes[outcome][book]) : null;
          const points = outcomeDetails[outcome][`${book}Points`];
          if (price !== null) {
            const parsedPoints = (points !== undefined && points !== null && points !== '') ? parseFloat(points) : null;
            entries.push({
              book, price,
              points: (parsedPoints !== null && !Number.isNaN(parsedPoints)) ? parsedPoints : null,
              logo: outcomeDetails[outcome][`${book}Logo`],
              lineDisplay: outcomeDetails[outcome][`${book}LineDisplay`],
            });
          }
        });

        let bestEntry = null;
        if (entries.length > 0) {
          entries.sort((a, b) => {
            const pA = (a.points === null || Number.isNaN(a.points)) ? 0 : a.points;
            const pB = (b.points === null || Number.isNaN(b.points)) ? 0 : b.points;
            if (pA !== pB) return outcome === 'Over' ? pA - pB : pB - pA;
            return b.price - a.price;
          });
          bestEntry = entries[0];
        }

        if (bestEntry) {
          bestOdds[outcome] = {
            price: bestEntry.price, points: bestEntry.points,
            logo: bestEntry.logo, lineDisplay: bestEntry.lineDisplay,
            meta: outcomeDetails[outcome][`${bestEntry.book}Meta`] || null,
          };
          row.bestOddsBookmakers[outcome] = bestEntry.book;
        } else {
          bestOdds[outcome] = { price: null, points: null, logo: '', lineDisplay: '' };
          row.bestOddsBookmakers[outcome] = '';
        }
      });

      // Build per-book cell data (only for selectedBooks, not all availableBooks)
      booksToRender.forEach(book => {
        const odds = { updatedDates: {}, meta: {}, limits: {} };
        ['Over', 'Under'].forEach(outcome => {
          const price = outcomes[outcome][book] ? parseFloat(outcomes[outcome][book]) : null;
          if (price !== null) {
            const fmt = fmtPrice(price, oddsFormat);
            const line = outcomeDetails[outcome][`${book}LineDisplay`] || '';
            odds[outcome] = line ? `${line} ${fmt}` : fmt;
            odds.updatedDates[outcome] = outcomeUpdatedDates[outcome][book];
            odds.meta[outcome] = outcomeDetails[outcome][`${book}Meta`] || null;
            odds.limits[outcome] = outcomeDetails[outcome][`${book}Limits`] || null;
          } else {
            odds[outcome] = '';
            odds.updatedDates[outcome] = null;
            odds.meta[outcome] = null;
            odds.limits[outcome] = null;
          }
        });
        row.bookmakerOdds[book] = odds;
        row[book] = '';
      });

      // Format best odds display
      ['Over', 'Under'].forEach(outcome => {
        const { price, lineDisplay } = bestOdds[outcome] || {};
        if (price !== null && price !== undefined) {
          const fmt = fmtPrice(price, oddsFormat);
          bestOdds[outcome].display = lineDisplay ? `${lineDisplay} ${fmt}` : fmt;
        } else {
          bestOdds[outcome].display = '';
        }
      });

      row.bestOdds = bestOdds;
      rowsArray.push(row);
    });

    // Sort by start time, then player name
    rowsArray.sort((a, b) => {
      const dateA = a.eventStart ? new Date(a.eventStart).getTime() : Infinity;
      const dateB = b.eventStart ? new Date(b.eventStart).getTime() : Infinity;
      if (dateA !== dateB) return dateA - dateB;
      return (a.playerName || '').localeCompare(b.playerName || '');
    });

    // Search filter: player name, team abbrs, event
    const q = searchText.trim().toLowerCase();
    const searchedRows = !q ? rowsArray : rowsArray.filter(r => {
      const hay = `${r.playerName || ''} ${r.shortName || ''} ${r.eventName || ''} ${r.awayAbbr || ''} ${r.homeAbbr || ''}`.toLowerCase();
      return q.split(/\s+/).every(term => hay.includes(term));
    });

    return { rows: searchedRows, isNoPointsBetType: noPoints };
  }, [throttledBets, sport, betType, periodType, selectedMatchup, selectedBooks, bookLogos, teamMap, liveVisible, oddsFormat, searchText]);

  // ══════════════════════════════════════════════════════════
  //  COLUMN ORDER PERSISTENCE
  // ══════════════════════════════════════════════════════════

  const getSavedColumnOrder = useCallback(() => {
    try {
      const saved = localStorage.getItem('propsColumnOrder');
      if (saved) return JSON.parse(saved);
    } catch (e) { /* ignore */ }
    return null;
  }, []);

  const saveColumnOrder = useCallback((order) => {
    try {
      localStorage.setItem('propsColumnOrder', JSON.stringify(order));
    } catch (e) { /* ignore */ }
  }, []);

  // ══════════════════════════════════════════════════════════
  //  COLUMNS — only depends on layout/book-related state.
  //  isNoPointsBetType is read via ref to avoid rebuilding
  //  columns when only the bet type's "has points" status changes.
  // ══════════════════════════════════════════════════════════

  const isNoPointsRef = useRef(isNoPointsBetType);
  isNoPointsRef.current = isNoPointsBetType;

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: 'eventName',
        headerName: '',
        minWidth: isMobile ? 135 : 165,
        flex: 1,
        sortable: false,
        disableReorder: true,
        renderCell: (params) => {
          const isClickable = (sport === 'NFL' || sport === 'NBA') && params.row.playerId;
          const noPoints = isNoPointsRef.current;
          return (
            <Box sx={SX_COL_OUTER}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: noPoints ? '70px' : '35px', pl: isMobile ? '4px' : '8px' }}>
                <Typography
                  onClick={isClickable ? (e) => { e.stopPropagation(); handlePlayerClick(params.row); } : undefined}
                  sx={{
                    fontSize: isMobile ? '12px' : '14px',
                    textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '100%',
                    cursor: isClickable ? 'pointer' : 'default',
                    '&:hover': isClickable ? { textDecoration: 'underline' } : {},
                  }}
                >
                  {params.row.displayName}
                </Typography>
              </Box>
              {!noPoints && (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '35px', pl: isMobile ? '4px' : '8px' }}>
                  {teamMap[params.row.awayTeamId]?.logo && (
                    <img src={teamMap[params.row.awayTeamId]?.logo} alt="" style={{ width: isMobile ? '16px' : '24px', height: isMobile ? '16px' : '24px', objectFit: 'contain', marginRight: '2px' }} />
                  )}
                  <Typography sx={{ fontSize: isMobile ? '11px' : '14px' }}>{params.row.awayAbbr}</Typography>
                  <Typography sx={{ fontSize: isMobile ? '10px' : '12px', mx: '2px' }}>@</Typography>
                  {teamMap[params.row.homeTeamId]?.logo && (
                    <img src={teamMap[params.row.homeTeamId]?.logo} alt="" style={{ width: isMobile ? '16px' : '24px', height: isMobile ? '16px' : '24px', objectFit: 'contain', marginRight: '2px' }} />
                  )}
                  <Typography sx={{ fontSize: isMobile ? '11px' : '14px' }}>{params.row.homeAbbr}</Typography>
                </Box>
              )}
            </Box>
          );
        },
      },
      {
        field: 'eventStartFormatted',
        headerName: '',
        minWidth: isMobile ? 70 : 85,
        maxWidth: isMobile ? 70 : 85,
        sortable: false,
        disableReorder: true,
        renderCell: (params) => (
          <Box sx={SX_COL_OUTER}>
            <Box sx={{ height: '35px', display: 'flex', alignItems: 'center', pl: isMobile ? '0px' : '8px', pr: isMobile ? '4px' : '8px' }}>
              <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{params.value.date}</Typography>
            </Box>
            <Box sx={{ height: '35px', display: 'flex', alignItems: 'center', pl: isMobile ? '0px' : '8px', pr: isMobile ? '4px' : '8px' }}>
              {params.row.isLive ? (
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  px: '6px', py: '1px', borderRadius: '4px',
                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.5)',
                }}>
                  <Box sx={{
                    width: '6px', height: '6px', borderRadius: '50%',
                    backgroundColor: '#ef4444',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.4 },
                    },
                  }} />
                  <Typography sx={{ fontSize: isMobile ? '10px' : '11px', fontWeight: 600, color: '#ef4444', letterSpacing: '0.5px' }}>
                    LIVE
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{params.value.time}</Typography>
              )}
            </Box>
          </Box>
        ),
      },
      {
        field: 'bestOdds',
        headerName: 'Best Odds',
        headerAlign: 'right',
        minWidth: isMobile ? 110 : 155,
        flex: 1,
        sortable: true,
        disableReorder: true,
        sortComparator: (v1, v2) => {
          const price1 = v1?.Over?.price ?? -Infinity;
          const price2 = v2?.Over?.price ?? -Infinity;
          return price1 - price2;
        },
        renderHeader: () => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', pr: isMobile ? '4px' : '8px' }}>
            <Typography sx={{ fontSize: isMobile ? '14px' : '16px', color: '#ffffff' }}>Best Odds</Typography>
          </Box>
        ),
        renderCell: (params) => {
          const value = params.value;
          const noPoints = isNoPointsRef.current;
          const outcomes = ['Over', 'Under'].filter(o => value[o]?.display);
          return (
            <Box sx={SX_COL_OUTER}>
              {outcomes.map(outcome => {
                const { display, logo, meta } = value[outcome] || {};
                if (!display) return null;
                return (
                  <Box key={outcome} sx={{
                    display: 'flex', alignItems: 'center', height: noPoints ? '70px' : '35px', pr: '8px',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                  }} onClick={() => meta && openOddsHistory(meta, params.row.bestOddsBookmakers?.[outcome])}>
                    <Typography sx={{ fontSize: isMobile ? '12px' : '14px', textAlign: 'right', pr: isMobile ? '4px' : '8px', flex: 1 }}>
                      {display}
                    </Typography>
                    {logo && <Tooltip title={params.row.bestOddsBookmakers?.[outcome] || ''} placement="top" arrow><img src={logo} alt="" style={STYLE_BEST_IMG} /></Tooltip>}
                  </Box>
                );
              })}
            </Box>
          );
        },
      },
    ];

    // ── Bookmaker columns ──
    const visibleBookmakers = selectedBooks;
    const bookmakerColumns = {};

    visibleBookmakers.forEach(book => {
      bookmakerColumns[book] = {
        field: book, headerName: '', headerAlign: 'right',
        minWidth: isMobile ? 90 : 115, flex: 1, sortable: false, disableReorder: false,
        renderHeader: () => {
          const hasLink = !!bookmakerLinks[book];
          return (
            <Box
              sx={{
                display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
                width: '100%', height: '100%', paddingRight: isMobile ? '4px' : '12px',
                cursor: 'grab', '&:active': { cursor: 'grabbing' }, userSelect: 'none',
              }}
              onClick={(e) => {
                if (hasLink && e.detail > 0) window.open(bookmakerLinks[book], '_blank', 'noopener,noreferrer');
              }}
            >
              {bookLogos[book] ? (
                <Tooltip title={book} placement="bottom" arrow>
                  <img src={bookLogos[book]} alt={book}
                    style={{ maxWidth: isMobile ? '40px' : '60px', maxHeight: isMobile ? '28px' : '36px',
                      width: 'auto', height: 'auto', objectFit: 'contain', pointerEvents: 'none', userSelect: 'none' }} />
                </Tooltip>
              ) : (
                <Typography sx={{ fontSize: isMobile ? '10px' : '12px', color: '#ffffff', textAlign: 'right' }}>{book}</Typography>
              )}
            </Box>
          );
        },
        renderCell: (params) => (
          <BookCell
            bookOdds={params.row.bookmakerOdds && params.row.bookmakerOdds[params.field]}
            best={params.row.bestOddsBookmakers}
            field={params.field}
            noPoints={isNoPointsRef.current}
            showVolume={showVolumeRef.current}
            isMobile={isMobile}
            onOpen={openOddsHistory}
          />
        ),
      };
    });

    // Apply saved column order
    const savedOrder = getSavedColumnOrder();
    const finalColumns = [...baseColumns];
    if (savedOrder && savedOrder.bookmakers) {
      savedOrder.bookmakers.forEach(book => { if (bookmakerColumns[book]) finalColumns.push(bookmakerColumns[book]); });
      visibleBookmakers.forEach(book => {
        if (!savedOrder.bookmakers.includes(book) && bookmakerColumns[book]) finalColumns.push(bookmakerColumns[book]);
      });
    } else {
      visibleBookmakers.forEach(book => { finalColumns.push(bookmakerColumns[book]); });
    }

    return finalColumns;
  }, [sport, isMobile, selectedBooks, bookLogos, teamMap, getSavedColumnOrder, showVolume]);

  // ── Column reorder handler ──
  // Subscribe to header drag lifecycle so data flushes pause during a reorder.
  const gridMounted = tableRows.length > 0;
  useEffect(() => {
    const api = apiRef.current;
    if (!gridMounted || !api || typeof api.subscribeEvent !== 'function') return undefined;
    let safety = null;
    const root = api.rootElementRef?.current;
    const endDrag = () => {
      if (safety) { clearTimeout(safety); safety = null; }
      if (!dragActiveRef.current) return;
      dragActiveRef.current = false;
      if (root) root.classList.remove('ss-col-dragging');
      if (dragPendingRef.current) {
        dragPendingRef.current = false;
        dirtyRef.current = false;
        setThrottledBets(latestBetsRef.current());
      }
    };
    const startDrag = () => {
      dragActiveRef.current = true;
      if (root) root.classList.add('ss-col-dragging');
      // Safety valve: never hold updates longer than 15s if dragend is lost
      if (safety) clearTimeout(safety);
      safety = setTimeout(endDrag, 15000);
    };
    // Also hold updates from pointer-down on a header: the browser needs a few
    // quiet frames to initiate a native drag, and a data flush in that window
    // (or a long render already in flight) prevents the drag from ever starting.
    const onPointerDown = (e) => {
      if (e.target && e.target.closest && e.target.closest('.MuiDataGrid-columnHeader')) startDrag();
    };
    const onPointerUp = () => { setTimeout(endDrag, 0); };
    if (root) root.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUp);
    const unsubs = [
      api.subscribeEvent('columnHeaderDragStart', startDrag),
      api.subscribeEvent('columnHeaderDragEnd', endDrag),
    ];
    return () => {
      if (root) root.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      unsubs.forEach(u => { try { u(); } catch (e) { /* ignore */ } });
      endDrag();
    };
  }, [apiRef, gridMounted]);

  const handleColumnOrderChange = useCallback(() => {
    if (!apiRef.current) return;
    try {
      const orderedFields = apiRef.current.getAllColumns().map(c => c.field);
      const bookmakers = orderedFields.filter(f => !FIXED_COLUMNS.includes(f));
      saveColumnOrder({ fixed: FIXED_COLUMNS, bookmakers });
    } catch (e) { /* ignore */ }
  }, [apiRef, saveColumnOrder]);

  // ══════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════

  // ── Filter controls (shared between mobile & desktop layouts) ──
  const leagueSelect = (
    <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? 0 : 120, flex: isMobile ? 1 : 'none' }}>
      <InputLabel id="league-select-label" shrink sx={{ color: '#ffffff' }}>League</InputLabel>
      <Select labelId="league-select-label" label="League" notched value={sport} onChange={(e) => setSport(e.target.value)}>
        {leagueOptions.map(league => (
          <MenuItem key={league} value={league}>{league}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
  const betTypeSelect = (
    <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? 0 : 120, flex: isMobile ? 1 : 'none' }}>
      <InputLabel id="bet-type-select-label" shrink sx={{ color: '#ffffff' }}>Bet Type</InputLabel>
      <Select labelId="bet-type-select-label" label="Bet Type" notched value={betType} onChange={(e) => setBetType(e.target.value)}>
        {betTypes.map(market => (
          <MenuItem key={market} value={market}>
            {marketNameMap.get(market) || market}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
  const periodSelect = (
    <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? 0 : 120, flex: isMobile ? 1 : 'none' }}>
      <InputLabel id="period-type-select-label" shrink sx={{ color: '#ffffff' }}>Period Type</InputLabel>
      <Select labelId="period-type-select-label" label="Period Type" notched value={periodType} onChange={(e) => setPeriodType(e.target.value)}>
        {periodTypes.map(type => (
          <MenuItem key={type} value={type}>{type}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
  const booksSelect = (
    <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? 0 : 140, flex: isMobile ? 1 : 'none' }}>
      <InputLabel id="books-filter-label" shrink sx={{ color: '#ffffff' }}>Books</InputLabel>
      <Select multiple labelId="books-filter-label" label="Books" notched value={selectedBooks} onChange={handleBooksFilterChange} renderValue={renderBooksValue}
        MenuProps={{ autoFocus: false, disableAutoFocusItem: true, PaperProps: { style: { maxHeight: 320 } } }}>
        <MenuItem value="all">
          <Checkbox checked={availableBooks.length > 0 && selectedBooks.length === availableBooks.length} />
          <ListItemText primary="Select All" />
        </MenuItem>
        {/* Combo presets — pick exactly the books of one category */}
        {BOOK_GROUPS.map(g => {
          const matches = booksInGroup(g, availableBooks);
          if (matches.length === 0) return null;
          return (
            <MenuItem key={g.label} value={GROUP_PREFIX + g.label} dense sx={{ borderBottom: g.label === "Pick'ems" ? '1px solid #444' : 'none' }}>
              <ListItemText
                primary={`${g.label} (${matches.length})`}
                sx={{ '& .MuiTypography-root': { fontSize: '0.8rem', fontStyle: 'italic', color: '#ff8a80' } }}
              />
            </MenuItem>
          );
        })}
        {availableBooks.map(book => (
          <MenuItem key={book} value={book}>
            <Checkbox checked={selectedBooks.includes(book)} />
            <ListItemText primary={book} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
  const searchField = (
    <TextField
      value={searchText} onChange={(e) => setSearchText(e.target.value)}
      placeholder="Search player/team" size="small" disabled={!isAuthenticated}
      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#BBBBBB' }} /></InputAdornment> }}
      sx={{
        width: isMobile ? 'auto' : 200, flex: isMobile ? 1 : 'none', minWidth: 0,
        '& .MuiInputBase-input': { color: '#ffffff' },
        '& .MuiInputBase-input::placeholder': { color: '#BBBBBB', opacity: 1 },
      }}
    />
  );
  const volCheck = (
    <FormControlLabel
      control={<Checkbox checked={showVolume} onChange={() => setShowVolume(v => !v)} sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' }, p: '4px' }} />}
      label={<Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Vol</Typography>}
      sx={{ ml: 0, mr: 0 }}
    />
  );
  const liveCheck = (
    <FormControlLabel
      control={<Checkbox checked={liveVisible} disabled={!isAuthenticated} onChange={() => setShowLive(v => !v)} sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' }, '&.Mui-disabled': { color: '#555555' }, p: '4px' }} />}
      label={<Typography sx={{ color: isAuthenticated ? '#ffffff' : '#777777', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{isMobile ? 'Live' : 'Show Live'}</Typography>}
      sx={{ ml: 0, mr: 0 }}
    />
  );

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden', width: '100%', position: 'relative' }}>
          <Box className="filter-section" sx={{ padding: isMobile ? '8px' : '16px', position: 'sticky', top: 0, zIndex: 3, backgroundColor: 'background.default' }}>
            {isMobile ? (
              <Stack direction="column" spacing={0.75}>
                {/* Always visible: search + filter toggle */}
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {searchField}
                  <IconButton
                    onClick={() => setMobileFiltersOpen(o => !o)}
                    sx={{
                      color: mobileFiltersOpen ? '#ff1744' : '#ffffff',
                      border: '1px solid #555', borderRadius: 1, p: '6px', flexShrink: 0,
                    }}
                  >
                    <FilterListIcon sx={{ fontSize: '1.2rem' }} />
                  </IconButton>
                </Stack>
                {/* Collapsible: dropdowns + checkboxes */}
                <Collapse in={mobileFiltersOpen}>
                  <Stack direction="column" spacing={0.75}>
                    <Stack direction="row" spacing={0.75}>
                      {leagueSelect}
                      {betTypeSelect}
                    </Stack>
                    <Stack direction="row" spacing={0.75}>
                      {periodSelect}
                      {booksSelect}
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {volCheck}
                      {liveCheck}
                    </Stack>
                  </Stack>
                </Collapse>
              </Stack>
            ) : (
              <Stack direction="row" spacing={2} alignItems="center">
                {leagueSelect}
                {betTypeSelect}
                {periodSelect}
                {booksSelect}
                {searchField}
                {volCheck}
                {liveCheck}
              </Stack>
            )}
            <Box sx={{ backgroundColor: 'background.default', marginTop: isMobile ? '8px' : '0' }}>
              <Tabs value={selectedMatchup} onChange={(e, newValue) => setSelectedMatchup(newValue)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: '32px' }}>
                <Tab label="All" value="All" sx={{ minHeight: '32px', padding: '6px 12px', fontSize: isMobile ? '12px' : '14px' }} />
                {matchups.map(matchup => (
                  <Tab
                    key={matchup.shortName}
                    value={matchup.shortName}
                    sx={{ minHeight: '32px', padding: '6px 12px', fontSize: isMobile ? '12px' : '14px' }}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {teamMap[matchup.awayTeamId]?.logo ? (
                          <img src={teamMap[matchup.awayTeamId]?.logo} alt="" style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
                        ) : (
                          <Typography sx={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 600 }}>{matchup.awayAbbr}</Typography>
                        )}
                        <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>@</Typography>
                        {teamMap[matchup.homeTeamId]?.logo ? (
                          <img src={teamMap[matchup.homeTeamId]?.logo} alt="" style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
                        ) : (
                          <Typography sx={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 600 }}>{matchup.homeAbbr}</Typography>
                        )}
                      </Box>
                    }
                  />
                ))}
              </Tabs>
            </Box>
          </Box>
          <Box className="table-section" sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <Box sx={{ width: '100%', height: '100%' }}>
              {tableRows.length === 0 ? (
                <Box className="empty-state" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1">No prop bets available for the selected filters.</Typography>
                </Box>
              ) : (
                <Box sx={{ height: '100%', width: '100%' }}>
                  <DataGridPro
                    apiRef={apiRef}
                    rows={tableRows}
                    columns={columns}
                    pinnedColumns={{ left: ['eventName'] }}
                    disableSelectionOnClick
                    disableRowSelectionOnClick
                    disableColumnMenu
                    disableColumnSelector
                    disableColumnFilter
                    rowHeight={70}
                    keepColumnPositionIfDraggedOutside
                    onColumnOrderChange={handleColumnOrderChange}
                    sx={DATA_GRID_SX}
                    rowBuffer={5}
                    rowThreshold={2}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <GameLogModal
          open={gameLogModalOpen}
          onClose={handleCloseModal}
          playerData={selectedPlayerData}
          gameLogData={gameLogData}
          loading={gameLogLoading}
          sport={gameLogSportRef.current}
        />
        <OddsHistoryModal
          open={historyOpen}
          onClose={closeOddsHistory}
          data={historyData}
          loading={historyLoading}
          meta={historyMeta}
        />
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default Props;